import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { GraduationCap } from 'lucide-react';
import clsx from 'clsx';
import { apiRequest } from '../lib/api';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { SelectField } from '../components/ui/Field';
import { Pagination } from '../components/people/Pagination';
import { GatedSection } from '../components/shared/GatedSection';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { formatDate } from '../lib/format';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  PROBATION_SORT_COLUMNS,
  PROBATION_VERDICT_LABEL,
  PROBATION_VERDICT_TONE,
  STAGE_TONE_CLASSES,
  ProbationReviewDrawer,
  probationFilterConfig,
  probationSearchSchema,
  type ProbationListResponse,
  type ProbationReviewRow,
  type ProbationReviewInitialValues,
} from '../components/hiring';

const PAGE_SIZE = 25;

interface EditTarget {
  teacherId: string;
  reviewId: string;
  initial: ProbationReviewInitialValues;
}

const toInitialValues = (
  row: ProbationReviewRow,
): ProbationReviewInitialValues => ({
  weekNo: row.weekNo,
  periodStart: row.periodStart,
  periodEnd: row.periodEnd,
  verdict: row.verdict,
  ...(row.overallScore !== null ? { overallScore: row.overallScore } : {}),
  ...(row.improvementNotes !== null
    ? { improvementNotes: row.improvementNotes }
    : {}),
  ...(row.progressNotes !== null ? { progressNotes: row.progressNotes } : {}),
  ...(row.concerns !== null ? { concerns: row.concerns } : {}),
});

export const Route = createFileRoute('/_authed/hr/probation')({
  validateSearch: probationSearchSchema,
  component: ProbationListPage,
});

function ProbationListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

  // The single source of truth for the verdict / teacher filters + sort + page,
  // backed by the route's URL search params. The adapter narrows the route's
  // precisely-typed search/navigate surface to the toolkit's structural
  // `FilterRoute` contract; the route's own Zod `validateSearch` keeps the
  // boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigateRoute = Route.useNavigate();
      return (opts) =>
        navigateRoute({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(probationFilterConfig, filterRoute, {
    pageSize: PAGE_SIZE,
  });
  const { values, apiParams, setSort, setPage } = filters;

  // `weekNo` stays a sibling single-select (the backend filters it with a single
  // `1..12` value, so it can't be a comma-joined toolkit facet). It lives in the
  // same URL search contract (`probationSearchSchema`), so read/write it there —
  // via the route's own typed `useSearch`/`useNavigate` — and fold it into the
  // one query alongside `apiParams`.
  const weekNo = Route.useSearch().weekNo;
  const routeNavigate = Route.useNavigate();
  const setWeekNo = (next: number | undefined) => {
    void routeNavigate({
      search: (prev) => ({
        ...prev,
        weekNo: next,
        // Changing a filter resets pagination — mirror the toolkit's behaviour.
        page: undefined,
      }),
    });
  };

  // ONE query, driven by the toolkit's `apiParams` (verdict / teacherId / sort /
  // page) plus the sibling weekNo select.
  const listQuery = useQuery({
    queryKey: ['hiring', 'probation', apiParams, weekNo ?? null] as const,
    queryFn: ({ signal }) =>
      apiRequest<ProbationListResponse>('/api/hiring/probation', {
        signal,
        query: {
          ...apiParams,
          ...(weekNo !== undefined ? { weekNo } : {}),
        },
      }),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['hiring', 'probation'] });
    if (editTarget) {
      void queryClient.invalidateQueries({
        queryKey: ['hiring', 'probation', 'teacher', editTarget.teacherId],
      });
    }
  };

  const columns = useMemo<ColumnDef<ProbationReviewRow, unknown>[]>(
    () => [
      {
        id: 'teacher',
        enableSorting: false,
        header: 'Teacher',
        cell: (info) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-[color:var(--color-text)]">
              {info.row.original.teacher?.name ?? '—'}
            </p>
            <p className="truncate font-mono text-[10px] text-[color:var(--color-text-muted)]">
              {info.row.original.teacher?.employeeId ?? info.row.original.teacherId.slice(0, 8)}
            </p>
          </div>
        ),
      },
      {
        id: 'week',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Week"
            binding={PROBATION_SORT_COLUMNS.weekNo}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm tabular-nums">
            #{info.row.original.weekNo}
          </span>
        ),
      },
      {
        id: 'verdict',
        enableSorting: false,
        header: 'Verdict',
        cell: (info) => {
          const v = info.row.original.verdict;
          return (
            <span
              className={clsx(
                'rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                STAGE_TONE_CLASSES[PROBATION_VERDICT_TONE[v]],
              )}
            >
              {PROBATION_VERDICT_LABEL[v]}
            </span>
          );
        },
      },
      {
        id: 'period',
        enableSorting: false,
        header: 'Period',
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)] tabular-nums">
            {info.row.original.periodStart} → {info.row.original.periodEnd}
          </span>
        ),
      },
      {
        id: 'score',
        enableSorting: false,
        header: 'Score',
        cell: (info) =>
          info.row.original.overallScore !== null ? (
            <span className="text-sm tabular-nums">
              {info.row.original.overallScore}
            </span>
          ) : (
            <span className="text-xs text-[color:var(--color-text-subtle)]">—</span>
          ),
      },
      {
        id: 'created',
        enableSorting: false,
        header: 'Created',
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)] tabular-nums">
            {formatDate(info.row.original.createdAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        enableSorting: false,
        header: '',
        size: 48,
        cell: (info) => (
          <div className="flex justify-end">
            <EditIconButton
              label="Edit review"
              title="Edit this week's review"
              onClick={() => {
                setEditTarget({
                  teacherId: info.row.original.teacherId,
                  reviewId: info.row.original.id,
                  initial: toInitialValues(info.row.original),
                });
              }}
            />
          </div>
        ),
      },
    ],
    [values.sort, setSort],
  );

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const isLoadingList = listQuery.isLoading && !listQuery.data;

  return (
    <ListOrOutlet basePath="/hr/probation">
    <ListShell
      title="Probation reviews"
      description="Weekly check-ins for newly-onboarded teachers in their first 12 weeks."
    >
      <FilterBar
        config={probationFilterConfig}
        filters={filters}
        resultCount={total}
      />

      <div className="mb-4 max-w-xs">
        <SelectField
          label="Week"
          value={weekNo === undefined ? '' : String(weekNo)}
          onChange={(e) =>
            setWeekNo(e.target.value === '' ? undefined : Number(e.target.value))
          }
          options={[
            { value: '', label: 'All weeks' },
            ...Array.from({ length: 12 }, (_, i) => i + 1).map((n) => ({
              value: String(n),
              label: `Week ${n}`,
            })),
          ]}
        />
      </div>

      <GatedSection
        error={listQuery.error}
        title="Probation"
        onRetry={() => listQuery.refetch()}
      >
        <DataTable<ProbationReviewRow>
          data={items}
          columns={columns}
          isLoading={isLoadingList}
          getRowId={(row) => row.id}
          onRowClick={(row) =>
            navigate({
              to: '/hr/probation/$teacherId',
              params: { teacherId: row.teacherId },
              search: { focusReviewId: row.id },
            })
          }
          emptyState={
            <EmptyState
              icon={<GraduationCap size={28} aria-hidden="true" />}
              title="No probation reviews match these filters"
              description="Try clearing a filter. Reviews appear here as newly-onboarded teachers complete their weekly check-ins."
            />
          }
        />
        <Pagination
          page={values.page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      </GatedSection>

      <ProbationReviewDrawer
        open={editTarget !== null}
        teacherId={editTarget?.teacherId ?? null}
        reviewId={editTarget?.reviewId ?? null}
        initialValues={editTarget?.initial}
        onClose={() => setEditTarget(null)}
        onSaved={() => {
          setEditTarget(null);
          invalidate();
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}
